import React from 'react';
import classnames from 'classnames';
import lodash from 'lodash';
import { LoadingStatus } from 'components/common/LoadingStatus/LoadingStatus';
import { GoBack } from 'components/common/Link';
import { Conditional } from 'common/components/layout';
import { ComposablePageHeader, MainContent } from 'components/common/PageHeader/PageHeader';

import styles from './styles.scss';

export default function EditEventType({
    className,
    isLoading,
    goBackTo,
    eventType,
    apiIntegrations
}) {
    if (isLoading) {
        return <LoadingStatus loading={isLoading} itemName="custom event type"/>;
    }

    const primaryDateField = getPrimaryDateField(eventType);
    const choosenApiIntegrations = getIntegrations(eventType, apiIntegrations);
    return (
        <main className={classnames(styles.parent, className)} data-cy="EditEventType">
            <GoBack to={goBackTo} className={styles.goBack} data-cy="EditEventType-GoBack">
                Back to Event Types
            </GoBack>
            <ComposablePageHeader className={styles.title}>
                <MainContent>
                    <h1 className={styles.title}>Custom Event Type</h1>
                </MainContent>
            </ComposablePageHeader>
            <div className={styles.body}>
                <PageSection data-cy="EditEventType-PageSection">
                    <FieldDescription title='Event Title' description={eventType.displayName} />
                    <FieldDescription
                        title='Event Description'
                        description={eventType.description}
                    />
                </PageSection>
                <PageSection title='Primary Date'>
                    <FieldDescription
                        data-cy="EditEventType-FieldDescription"
                        title={primaryDateField.displayName}
                        description={primaryDateField.description}
                    />
                </PageSection>
                <PageSection title='Display Settings' data-cy="EditEventType-Settings">
                    { 
                        getNonPrimaryDateFields(eventType).map(
                            (field, index) => (
                                <FieldDescription
                                    key={index}
                                    title={field.displayName}
                                    description={field.description}
                                />
                            )
                        )
                    }
                </PageSection>
                <PageSection title='Integration Partners' withBorder={false} data-cy="EditEventType-Partners">
                    { 
                        choosenApiIntegrations.map(
                            (integration, index) => (
                                <FieldDescription
                                    key={index}
                                    title={integration.name}
                                    description={integration.description}
                                    defaultDescription=''
                                />
                            )
                        )
                    }
                </PageSection>
            </div>
        </main>
    );
}

export const getPrimaryDateField = (eventType) => {
    if (lodash.isEmpty(eventType.fields)) {
        return {};
    }

    return eventType.fields.find(field => field.id === eventType.primaryDateField) || {};
};

export const getNonPrimaryDateFields = (eventType) => { 
    if (lodash.isEmpty(eventType.fields)) {
        return [];
    }

    return eventType.fields.filter(field => field.id !== eventType.primaryDateField) || [];
};

export const getIntegrations = (eventType, apiConfigurations) => { 
    if (lodash.isEmpty(eventType.integrationPartnerIds)) {
        return [{name: '', description: '-'}];
    }

    return lodash.intersectionWith(
        apiConfigurations,
        eventType.integrationPartnerIds,
        (ac, id) => ac.mappingId === id,
    );
};

const PageSection = ({ title, children, withBorder = true }) => (
    <div className={classnames(styles.pageSection, {[styles.sectionBorder]: withBorder})} data-cy="EditEventType-PageSections">
        <Conditional condition={title}>
            <h2 data-cy="EditEventType-h2">{title}</h2>
        </Conditional>
        { children }
    </div>
);

export const FieldDescription = ({ title, description, defaultDescription = '-' }) => (
    <div className={styles.fieldDescription} data-cy="EditEventType-FieldDescription">
        <Conditional condition={title}>
            <h3 data-cy="EditEventType-h3">
                {title}
            </h3>
        </Conditional>
        <Conditional condition={description || defaultDescription}>
            <h4 data-cy="EditEventType-h4">
                {description || defaultDescription}
            </h4>
        </Conditional>
    </div>
);
